import { FALLBACK_INWORLD_VOICES, INWORLD_VOICE_ID_PATTERN, LANGUAGE_INFO } from '../../Mobx/AudioCatalog';

/**
 * The logic of the "bulk run" tab of the audio page (SCRUM-1847): reading what an admin typed, building the request for the
 * server, and the words for what the server answers. No React and no network here, so it is tested on its own.
 *
 * The same request is sent to plan a run (a dry run that creates nothing) and to start it - the start adds the number of files
 * that the plan showed: expectedMissingFiles (mode fill-missing) or expectedReplaceFiles (mode replace-with-backup).
 * The rules of the request are the ones of 4_services/ai/text_to_speech/bulk/config.js on the server, which checks everything again.
 *
 * Two modes: fill-missing creates the files that are missing (a file that exists is never touched); replace-with-backup replaces the files
 * that exist, each one only after its old version was copied to a backup folder of the run (the server never deletes anything).
 */

export const MODE_FILL_MISSING = 'fill-missing';
export const MODE_REPLACE_WITH_BACKUP = 'replace-with-backup';
export const MODES = [MODE_FILL_MISSING, MODE_REPLACE_WITH_BACKUP];
export const MODE_LABELS = { [MODE_FILL_MISSING]: 'יצירת קבצים חסרים', [MODE_REPLACE_WITH_BACKUP]: 'החלפה עם גיבוי' };
/** Where the old files of a replacement run are kept: <prefix><run id>/ (the server sends the same in /options) */
export const BACKUP_PREFIX = 'files-backup/audio/';
/** A path in a Hebrew sentence stays in one piece (it moves to the next line as a whole, and is broken only when the line is shorter than the path) */
export const PATH_STYLE = { display: 'inline-block', maxWidth: '100%', overflowWrap: 'anywhere' };
export const SOURCE_IDS = 'ids';
export const SOURCE_FILTER = 'filter';
export const GENDERS = ['female', 'male'];
export const GENDER_LABELS = { female: 'נקבה', male: 'זכר' };
export const LISTS = ['A', 'B', 'C', 'D', 'E'];
export const PROVIDER_LABELS = { google: 'Google (Gemini)', inworld: 'Inworld' };
/** For a narrow place (a cell of a table, next to a voice) */
export const PROVIDER_SHORT_LABELS = { google: 'Google', inworld: 'Inworld' };
export const OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;

/** What the server allows - used until the options are loaded (the numbers of config.js on the server) */
export const FALLBACK_LIMITS = { maxEntries: 2000, maxFiles: 5000, maxReplaceFiles: 1000, maxCostUsd: 50, maxRpm: 120, maxConcurrency: 4, defaultEntryLimit: 500 };
export const FALLBACK_PACING = { google: { rpm: 8, concurrency: 1 }, inworld: { rpm: 120, concurrency: 3 } };

/** The fields of an entry that a bulk run can speak, and the languages that come from each of them (the server sends the same) */
const FALLBACK_FIELD_LANGUAGES = {
    heb_to_eng: { original: ['he'], trns: ['en_US', 'en_GB'] },
    eng_to_heb: { original: ['en_US', 'en_GB'], trns: ['he'] },
};

/** The two voices that were chosen for Hebrew with Google; Gemini voices speak every language */
const SUGGESTED_GOOGLE_VOICES = { female: 'Callirrhoe', male: 'Enceladus' };

export const languageLabel = (language) => LANGUAGE_INFO[language]?.label ?? language;

/** "עברית: Callirrhoe (נקבה), Enceladus (זכר)" - who speaks, for every language of a plan or a run */
export const describeVoices = (voices) => Object.entries(voices ?? {})
    .map(([language, byGender]) => `${languageLabel(language)}: ${Object.entries(byGender ?? {}).map(([gender, voice]) => `${voice} (${GENDER_LABELS[gender] ?? gender})`).join(', ')}`)
    .join(' | ');

export const hebrewField = (dictionary) => (dictionary === 'heb_to_eng' ? 'original' : 'trns');

/** The mode of a plan or a run. A server that does not say (it does not have the mode of replacement yet) creates missing files. */
export const modeOf = (item) => item?.mode ?? item?.config?.mode ?? MODE_FILL_MISSING;
export const isReplaceMode = (mode) => mode === MODE_REPLACE_WITH_BACKUP;
/** Creating is always possible; a replacement only when the server says that it knows the mode (/options holds modes) */
export const supportsMode = (options, mode) => mode === MODE_FILL_MISSING || (Array.isArray(options?.modes) && options.modes.includes(mode));
export const backupPrefixOf = (options) => options?.backupPrefix ?? BACKUP_PREFIX;
/** "files-backup/audio/<run id>/" - where the old files of a replacement run will be, before the run has an id */
export const backupFolderPattern = (options) => `${backupPrefixOf(options)}<run id>/`;
/** The files that a plan works on: the ones it would create, or the ones it would replace */
export const plannedFiles = (plan) => (isReplaceMode(modeOf(plan)) ? (plan?.replace?.files ?? plan?.files?.existing ?? 0) : (plan?.files?.missing ?? 0));
/** The field that a start must hold: the number of files that the plan showed, under the name of its mode */
export const expectedFilesField = (request) => (isReplaceMode(request?.mode) ? 'expectedReplaceFiles' : 'expectedMissingFiles');

export const limitsOf = (options) => ({ ...FALLBACK_LIMITS, ...(options?.limits ?? {}) });
export const pacingOf = (options, provider) => options?.pacing?.[provider] ?? FALLBACK_PACING[provider] ?? FALLBACK_PACING.google;

// ____________________________________________________________________________________________
// reading what was typed

/** "1,234" - a number the way it is read in the page */
export const formatNumber = (value) => Number(value ?? 0).toLocaleString('he-IL');

/** The estimate of the time of a run, from minutes: "פחות מדקה", "דקה אחת", "25 דקות", "כ-10.4 שעות (625 דקות)" */
export function formatDuration(minutes) {
    const value = Number(minutes);
    if (!Number.isFinite(value) || value <= 0) return 'פחות מדקה';
    const rounded = Math.round(value);
    if (rounded <= 1) return value < 0.5 ? 'פחות מדקה' : 'דקה אחת';
    if (rounded < 90) return `${formatNumber(rounded)} דקות`;
    return `כ-${(value / 60).toLocaleString('he-IL', { maximumFractionDigits: 1 })} שעות (${formatNumber(rounded)} דקות)`;
}

/** "$0.0204" for a tiny amount, "$1.50" otherwise */
export function formatUsd(value) {
    const number = Number(value ?? 0);
    if (!Number.isFinite(number)) return '$0.00';
    return `$${number > 0 && number < 0.01 ? number.toFixed(4) : number.toFixed(2)}`;
}

/** "3.11.2026, 20:41:05" in the time of the browser, or '' */
export function formatDateTime(value) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('he-IL');
}

/**
 * The ids that were pasted - separated by spaces, new lines, commas or semicolons. Quotes and brackets around an id (a list
 * copied from JSON) are ignored. Capital letters become small ones, and an id that appears twice counts once.
 * @returns {{ ids: string[], invalid: string[], duplicates: number }}
 */
export function parseIds(text) {
    const tokens = String(text ?? '').split(/[\s,;]+/)
        .map((token) => token.replace(/^[[\]"'`]+|[[\]"'`]+$/g, ''))
        .filter(Boolean);
    const seen = new Set();
    const ids = [];
    const invalid = [];
    let duplicates = 0;
    for (const token of tokens) {
        if (!OBJECT_ID_PATTERN.test(token)) {
            invalid.push(token);
            continue;
        }
        const id = token.toLowerCase();
        if (seen.has(id)) {
            duplicates += 1;
            continue;
        }
        seen.add(id);
        ids.push(id);
    }
    return { ids, invalid, duplicates };
}

/** Whole numbers separated by spaces, commas or semicolons: { values, invalid } */
export function parseIntegers(text) {
    const values = [];
    const invalid = [];
    for (const token of String(text ?? '').split(/[\s,;]+/).filter(Boolean)) {
        if (/^-?\d+$/.test(token)) {
            const number = Number(token);
            if (!values.includes(number)) values.push(number);
        } else {
            invalid.push(token);
        }
    }
    return { values, invalid };
}

/** '' -> undefined (not given), a number -> the number, anything else -> NaN */
const optionalNumber = (text) => {
    const trimmed = String(text ?? '').trim();
    if (trimmed === '') return undefined;
    return /^-?\d+(\.\d+)?$/.test(trimmed) ? Number(trimmed) : NaN;
};

// ____________________________________________________________________________________________
// the form

/** The languages that the fields of a dictionary are spoken in */
export function fieldLanguages(options, dictionary) {
    return options?.dictionaries?.find((item) => item.key === dictionary)?.fields ?? FALLBACK_FIELD_LANGUAGES[dictionary] ?? {};
}

export function impliedLanguages(options, dictionary, fields) {
    const map = fieldLanguages(options, dictionary);
    return [...new Set(fields.flatMap((field) => map[field] ?? []))];
}

export function initialForm(options, dictionary = options?.dictionaries?.[0]?.key ?? 'heb_to_eng') {
    const fields = [hebrewField(dictionary)];
    return {
        dictionary,
        mode: MODE_FILL_MISSING,
        source: SOURCE_IDS,
        idsText: '',
        bandsText: '',
        lists: [],
        priorityMin: '',
        priorityMax: '',
        createdFrom: '',
        createdTo: '',
        limit: '',
        afterId: '',
        fields,
        languages: impliedLanguages(options, dictionary, fields),
        genders: [...GENDERS],
        provider: 'google',
        // { google|inworld: { he|en_US|...: { female, male } } } - only what the admin chose; the rest comes from defaultVoice
        voices: {},
        useFallback: false,
        maxCostUsd: '',
        rpm: '',
        concurrency: '',
    };
}

/** Another dictionary: its Hebrew field is spoken, the other settings stay */
export function withDictionary(form, dictionary, options) {
    const fields = [hebrewField(dictionary)];
    return { ...form, dictionary, fields, languages: impliedLanguages(options, dictionary, fields) };
}

/** Other fields: a language that comes in with a new field is chosen, a language that the admin cleared stays cleared */
export function withFields(form, fields, options) {
    const before = impliedLanguages(options, form.dictionary, form.fields);
    const after = impliedLanguages(options, form.dictionary, fields);
    const languages = after.filter((language) => form.languages.includes(language) || !before.includes(language));
    return { ...form, fields, languages };
}

export function fallbackProviderOf(form) {
    return form.provider === 'google' ? 'inworld' : 'google';
}

/** The voice that is filled in before the admin chooses: the ones that were decided for the first runs, then the defaults of the server */
export function defaultVoice(options, provider, language, gender) {
    const suggested = options?.suggestedVoices?.[provider]?.[language]?.[gender];
    if (suggested) return suggested;
    if (provider === 'google') {
        const wanted = SUGGESTED_GOOGLE_VOICES[gender];
        const list = options?.voices?.google;
        return !list || list.includes(wanted) ? wanted : '';
    }
    return options?.voices?.inworld?.defaults?.[language]?.[gender]?.[0] ?? FALLBACK_INWORLD_VOICES[language]?.[gender]?.[0] ?? '';
}

export function voiceOf(form, options, provider, language, gender) {
    return form.voices?.[provider]?.[language]?.[gender] ?? defaultVoice(options, provider, language, gender);
}

export function withVoice(form, provider, language, gender, voice) {
    const voices = form.voices ?? {};
    const ofProvider = voices[provider] ?? {};
    const ofLanguage = ofProvider[language] ?? {};
    return { ...form, voices: { ...voices, [provider]: { ...ofProvider, [language]: { ...ofLanguage, [gender]: voice } } } };
}

export function isValidVoice(options, provider, voice) {
    if (typeof voice !== 'string' || voice === '') return false;
    if (provider === 'google') return options?.voices?.google ? options.voices.google.includes(voice) : true;
    let pattern = INWORLD_VOICE_ID_PATTERN;
    try {
        if (options?.voices?.inworld?.pattern) pattern = new RegExp(options.voices.inworld.pattern);
    } catch {
        // a pattern that cannot be read: the one of the admin is used
    }
    return pattern.test(voice);
}

/**
 * The request for the server, or the list of what is wrong in the form.
 * The order of the keys is fixed, so the same form always gives the same text (requestKey).
 * @returns {{ ok: boolean, errors: string[], request: object|null }}
 */
export function buildRequest(form, options) {
    const errors = [];
    const limits = limitsOf(options);
    const mode = MODES.includes(form.mode) ? form.mode : MODE_FILL_MISSING;
    const request = { dictionary: form.dictionary, mode };
    if (!supportsMode(options, mode)) {
        errors.push('השרת עוד לא מכיר את מצב ההחלפה עם גיבוי. אחרי שהשרת יתעדכן המצב הזה יהיה זמין, ועד אז אפשר ליצור רק קבצים שחסרים.');
    }

    // the entries
    if (form.source === SOURCE_IDS) {
        const { ids, invalid } = parseIds(form.idsText);
        if (ids.length === 0 && invalid.length === 0) errors.push('צריך להדביק לפחות מזהה אחד של ערך.');
        if (invalid.length > 0) errors.push(`${formatNumber(invalid.length)} מזהים אינם תקינים (מזהה הוא 24 תווים: ספרות ואותיות a עד f), למשל: ${invalid[0].slice(0, 30)}`);
        if (ids.length > limits.maxEntries) errors.push(`אפשר עד ${formatNumber(limits.maxEntries)} ערכים בהרצה אחת, והודבקו ${formatNumber(ids.length)}.`);
        request.ids = ids;
    } else {
        const filter = {};
        const bands = parseIntegers(form.bandsText);
        if (bands.invalid.length > 0) errors.push(`בנדים הם מספרים שלמים בלבד (נמצא: ${bands.invalid[0].slice(0, 20)}).`);
        else if (bands.values.length > 50) errors.push('אפשר עד 50 בנדים.');
        else if (bands.values.length > 0) filter.bands = bands.values;
        if (form.lists.length > 0) filter.lists = LISTS.filter((list) => form.lists.includes(list));
        for (const key of ['priorityMin', 'priorityMax']) {
            const value = optionalNumber(form[key]);
            if (Number.isNaN(value)) errors.push(`${key === 'priorityMin' ? 'עדיפות מ-' : 'עדיפות עד'} חייבת להיות מספר.`);
            else if (value !== undefined) filter[key] = value;
        }
        for (const key of ['createdFrom', 'createdTo']) {
            if (form[key]) filter[key] = form[key];
        }
        request.filter = filter;
        const limit = optionalNumber(form.limit);
        if (limit !== undefined) {
            if (!Number.isInteger(limit) || limit < 1 || limit > limits.maxEntries) errors.push(`מספר הערכים לבדיקה הוא מספר שלם מ-1 עד ${formatNumber(limits.maxEntries)}.`);
            else request.limit = limit;
        }
        const afterId = String(form.afterId ?? '').trim();
        if (afterId !== '') {
            if (OBJECT_ID_PATTERN.test(afterId)) request.afterId = afterId.toLowerCase();
            else errors.push('המשך אחרי מזהה: המזהה חייב להיות 24 תווים (ספרות ואותיות a עד f).');
        }
    }

    // what is spoken
    const fields = form.fields.filter(Boolean);
    if (fields.length === 0) errors.push('צריך לבחור לפחות שדה אחד להקראה.');
    request.fields = fields;
    const languages = impliedLanguages(options, form.dictionary, fields).filter((language) => form.languages.includes(language));
    if (fields.length > 0 && languages.length === 0) errors.push('צריך לבחור לפחות שפה אחת.');
    request.languages = languages;
    const genders = GENDERS.filter((gender) => form.genders.includes(gender));
    if (genders.length === 0) errors.push('צריך לבחור לפחות מגדר אחד.');
    request.genders = genders;

    // who speaks
    const collectVoices = (provider, label) => {
        const voices = {};
        for (const language of languages) {
            voices[language] = {};
            for (const gender of genders) {
                const voice = voiceOf(form, options, provider, language, gender).trim();
                if (!isValidVoice(options, provider, voice)) {
                    errors.push(`${label}: חסר קול תקין (${GENDER_LABELS[gender]}, ${language}).`);
                } else {
                    voices[language][gender] = voice;
                }
            }
        }
        return voices;
    };
    request.provider = form.provider;
    request.voices = collectVoices(form.provider, PROVIDER_LABELS[form.provider]);
    if (form.useFallback) {
        const provider = fallbackProviderOf(form);
        request.fallback = { provider, voices: collectVoices(provider, `גיבוי ${PROVIDER_LABELS[provider]}`) };
    }

    // the cost and the pace
    const maxCostUsd = optionalNumber(form.maxCostUsd);
    if (maxCostUsd !== undefined) {
        if (!(maxCostUsd > 0 && maxCostUsd <= limits.maxCostUsd)) errors.push(`תקרת העלות היא מספר מעל 0 ועד ${limits.maxCostUsd} דולר.`);
        else request.maxCostUsd = maxCostUsd;
    }
    for (const [key, label, max] of [['rpm', 'מספר הבקשות בדקה', limits.maxRpm], ['concurrency', 'מספר הבקשות במקביל', limits.maxConcurrency]]) {
        const value = optionalNumber(form[key]);
        if (value === undefined) continue;
        if (!Number.isInteger(value) || value < 1 || value > max) errors.push(`${label} הוא מספר שלם מ-1 עד ${max}.`);
        else request[key] = value;
    }

    return errors.length > 0 ? { ok: false, errors, request: null } : { ok: true, errors: [], request };
}

/** Two requests that are the same have the same key: the plan on the screen is the plan of the form only while the keys are equal */
export const requestKey = (request) => JSON.stringify(request);

// ____________________________________________________________________________________________
// what the server answers: the plan

const leadingNumber = (message) => Number.parseInt(String(message ?? '').match(/^\s*(\d+)/)?.[1] ?? '0', 10);

/**
 * A warning of the plan in Hebrew (the text of the server is English; an unknown kind of warning shows that text)
 * @param {object} warning
 * @param {string} [mode] the mode of the plan - a few words depend on it (a file is created, or replaced)
 */
export function describeWarning(warning, mode = MODE_FILL_MISSING) {
    const count = formatNumber(leadingNumber(warning?.message));
    switch (warning?.code) {
        case 'entries-not-found': return `${count} מהמזהים אינם ערכים במילון הזה, ולכן לא נכללו.`;
        case 'texts-skipped': return `${count} טקסטים ריקים או שיש בהם רק הערות וסימני פיסוק, ולכן אין מה להקריא והם לא נכללו.`;
        case 'edge-spaces-in-name': return `${count} קבצים ${isReplaceMode(mode) ? 'יוחלפו' : 'ייווצרו'} בשם שמתחיל או נגמר ברווח, כי האתר מבקש בדיוק את השם הזה. הרווחים נחתכים רק מהטקסט שמוקרא.`;
        case 'missing-not-replaced': return `${count} קבצים עוד לא קיימים, ולכן לא ייגעו בהם: מצב ההחלפה מחליף רק קבצים שקיימים. כדי ליצור אותם משתמשים במצב "${MODE_LABELS[MODE_FILL_MISSING]}".`;
        case 'browser-cache': return 'דפדפנים ששמרו כבר את הקובץ ימשיכו להשמיע את הישן עד שהמטמון שלהם יפוג (קובץ שהוחלף נשמר ליום אחד, קבצים ישנים עד שנה). בחלון פרטי שומעים את החדש. ב-S3 אין גרסאות, ולכן הגיבוי הוא הדרך היחידה לחזור אחורה.';
        case 'name-truncated': return `${count} שמות קבצים נחתכו ל-60 תווים, כמו שהאתר עושה.`;
        case 'same-name-different-text': return `${count} טקסטים מקבלים שם קובץ זהה לטקסט אחר אבל נקראים אחרת. יוקרא הראשון.`;
        case 'google-daily-quota': return 'למפתח Google ברמה 1 יש מכסה של כ-100 בקשות ביום לכל מודל. ההרצה תושהה במכסה היומית (או תעבור לגיבוי, אם הוגדר), ואפשר להמשיך אותה למחרת.';
        case 'fallback-not-ready': return `אי אפשר להשתמש בגיבוי כרגע. ${String(warning.message).replace(/^The fallback cannot be used now:\s*/, '')}`;
        default: return String(warning?.message ?? '');
    }
}

/** A blocker of the plan (the run cannot start while there is one) in Hebrew */
export function describeBlocker(blocker, plan, options) {
    const message = String(blocker?.message ?? '');
    switch (blocker?.code) {
        case 'not-ready': return `השרת לא מוכן להרצה: ${message}`;
        case 'cost-over-cap': return `העלות המשוערת (${formatUsd(plan?.estimatedCostUsd)}) גבוהה מהתקרה (${formatUsd(plan?.maxCostUsd)}). מעלים את התקרה (עד ${limitsOf(options).maxCostUsd} דולר) או מקטינים את ההרצה.`;
        case 'existence-unchecked': return `אי אפשר היה לבדוק ב-S3 אם ${formatNumber(leadingNumber(message))} קבצים קיימים, ולכן ההרצה לא תתחיל. אפשר לנסות שוב. (${message})`;
        case 'too-many-files': return isReplaceMode(modeOf(plan))
            ? `ההחלפה כוללת ${formatNumber(plan?.files?.existing)} קבצים קיימים, וההרצה מחליפה לכל היותר ${formatNumber(limitsOf(options).maxReplaceFiles)}. מקטינים את מספר הערכים, ומשתמשים ב"המשך אחרי מזהה" להרצה הבאה.`
            : `חסרים ${formatNumber(plan?.files?.missing)} קבצים, וההרצה יוצרת לכל היותר ${formatNumber(limitsOf(options).maxFiles)}. מקטינים את מספר הערכים, ומשתמשים ב"המשך אחרי מזהה" להרצה הבאה.`;
        case 'nothing-to-create': return 'כל הקבצים כבר קיימים, אין מה ליצור.';
        case 'nothing-to-replace': return `אף אחד מהקבצים לא קיים, ולכן אין מה להחליף. כדי ליצור קבצים משתמשים במצב "${MODE_LABELS[MODE_FILL_MISSING]}".`;
        default: return message;
    }
}

// ____________________________________________________________________________________________
// what the server answers: the runs

export const JOB_STATUS_LABELS = { running: 'רצה', paused: 'מושהית', stopped: 'נעצרה', completed: 'הסתיימה' };

export const PAUSE_REASON_TEXT = {
    quota: 'המכסה היומית של הספק נגמרה. אפשר להמשיך מחר, או להגדיר ספק גיבוי.',
    auth: 'הספק לא קיבל את המפתח. מעדכנים את המפתח בשרת, ואז ממשיכים.',
    credit: 'נגמר האשראי אצל הספק. מטעינים אשראי, ואז ממשיכים.',
    'cost-cap': 'ההרצה הגיעה לתקרת העלות. אפשר להמשיך עם תקרה גבוהה יותר.',
    'too-many-failures': 'יותר מדי קבצים נכשלו אחד אחרי השני. בודקים את השגיאות של הקבצים, ואז ממשיכים.',
    error: 'ההרצה הושהתה בגלל שגיאה. קוראים את ההודעה, ואז ממשיכים.',
    'server-restart': 'השרת הופעל מחדש, וההרצה הושהתה. היא ממשיכה מעצמה, ואם זה לא קרה אפשר להמשיך אותה כאן.',
    backup: 'אי אפשר היה לגבות קובץ ישן, ולכן שום קובץ לא הוחלף ושום דבר לא שולם. קוראים את ההודעה (בדרך כלל חסרה הרשאה ל-S3, או ש-S3 לא זמין), פותרים, ואז ממשיכים.',
};

export const ITEM_STATUS_LABELS = { pending: 'ממתין', working: 'בעבודה', done: 'נוצר', skipped: 'כבר היה קיים', failed: 'נכשל' };
/** The same states in a run that replaces files: a file that does not exist is not created, it is left alone */
export const REPLACE_ITEM_STATUS_LABELS = { pending: 'ממתין', working: 'בעבודה', done: 'הוחלף', skipped: 'אין קובץ להחלפה', failed: 'נכשל' };
export const ITEM_STATUSES = ['pending', 'working', 'done', 'skipped', 'failed'];
export const itemStatusLabels = (mode) => (isReplaceMode(mode) ? REPLACE_ITEM_STATUS_LABELS : ITEM_STATUS_LABELS);

/** How a file of a replacement run ended (item.outcome) - more exact than its state */
export const ITEM_OUTCOME_LABELS = { replaced: 'הוחלף', 'already-replaced': 'הוחלף בניסיון קודם של ההרצה', 'file-missing': 'אין קובץ, לא נוצר' };

/** What the two counters of a run count */
export const counterLabels = (mode) => (isReplaceMode(mode)
    ? { done: 'הוחלפו', skipped: 'אין קובץ להחלפה' }
    : { done: 'נוצרו', skipped: 'כבר היו' });

/** The words on the chip of a file of a run: how a replacement ended, when the server says; otherwise its state */
export function describeItem(item, mode) {
    if (isReplaceMode(mode) && item?.outcome && ITEM_OUTCOME_LABELS[item.outcome]) return ITEM_OUTCOME_LABELS[item.outcome];
    return itemStatusLabels(mode)[item?.status] ?? String(item?.status ?? '');
}

/** The folder with the old files of a replacement run (null for a run that only creates files) */
export const backupFolderOf = (job, options) => (isReplaceMode(modeOf(job)) ? (job?.backupFolder ?? `${backupPrefixOf(options)}${job?.id ?? ''}/`) : null);

export const ERROR_KIND_LABELS = {
    'rate-limit': 'מגבלת קצב',
    quota: 'מכסה יומית',
    auth: 'מפתח לא תקין',
    credit: 'אין אשראי',
    'bad-request': 'בקשה שנדחתה',
    transient: 'תקלה זמנית',
    backup: 'הגיבוי נכשל',
    'backup-conflict': 'מקום הגיבוי תפוס או שהקובץ השתנה',
    unknown: 'לא ידוע',
};

/** { label, severity } of a run - severity is the one of an MUI Chip / Alert: info, warning, success, error, default */
export function describeJob(job) {
    if (!job) return { label: '', severity: 'default' };
    if (job.stalled) return { label: 'נתקעה (ייתכן שהשרת הופעל מחדש)', severity: 'warning' };
    if (job.status === 'running') return { label: job.stopRequested ? 'נעצרת...' : JOB_STATUS_LABELS.running, severity: 'info' };
    if (job.status === 'paused') return { label: JOB_STATUS_LABELS.paused, severity: 'warning' };
    if (job.status === 'stopped') return { label: JOB_STATUS_LABELS.stopped, severity: 'default' };
    if (job.status === 'completed') {
        return (job.counters?.failed ?? 0) > 0
            ? { label: 'הסתיימה עם כשלונות', severity: 'error' }
            : { label: JOB_STATUS_LABELS.completed, severity: 'success' };
    }
    return { label: String(job.status ?? ''), severity: 'default' };
}

/** How far a run is: the files that are settled (created, found to exist already, or failed) out of all of them */
export function progressOf(job) {
    const counters = job?.counters ?? {};
    const total = counters.total ?? 0;
    const finished = (counters.done ?? 0) + (counters.skipped ?? 0) + (counters.failed ?? 0);
    return { total, finished, percent: total > 0 ? Math.min(100, Math.round((finished / total) * 100)) : 0 };
}

/** A run that works now: another one cannot start. */
export const isActiveJob = (job) => job?.status === 'running' && !job?.stalled;

/** "6 קבצים, 48 תווים" - what one provider made in a run */
export function describeUsage(usage) {
    return Object.entries(usage ?? {})
        .map(([provider, value]) => `${PROVIDER_LABELS[provider] ?? provider}: ${formatNumber(value?.files)} קבצים, ${formatNumber(value?.chars)} תווים`)
        .join(' | ');
}
